/**
 * The EMS call card.
 *
 * Laid out in the order the questions are actually asked on the phone: where,
 * what happened, is it safe, who is the patient, who pays. A call-taker should
 * be able to work top to bottom without hunting.
 *
 * Every field autosaves. There is no Save button, deliberately — the one thing
 * worse than an incomplete record is a complete one that was lost because the
 * call ended before anybody pressed anything.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type CasePatch, type ClientSummary } from '../api/client.js';
import { useAutosave, type SaveState } from './useAutosave.js';
import { AddressField } from './AddressField.js';
import { SchemePicker } from './SchemePicker.js';
import { OccFields } from './OccFields.js';

const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unknown', label: 'Unknown' },
];

const BREATHING = [
  { value: 'normal', label: 'Normal' },
  { value: 'difficulty', label: 'Difficulty' },
  { value: 'agonal', label: 'Agonal' },
  { value: 'not_breathing', label: 'Not breathing' },
  { value: 'unknown', label: 'Unknown' },
];

const AGE_BANDS = [
  { value: 'infant', label: 'Infant (under 1)' },
  { value: 'child', label: 'Child (1-12)' },
  { value: 'adolescent', label: 'Adolescent (13-17)' },
  { value: 'adult', label: 'Adult (18-64)' },
  { value: 'elderly', label: 'Elderly (65+)' },
  { value: 'unknown', label: 'Unknown' },
];

const LEVELS_OF_CARE = [
  { value: 'bls', label: 'BLS' },
  { value: 'ils', label: 'ILS' },
  { value: 'als', label: 'ALS' },
  { value: 'critical_care', label: 'Critical care' },
];

const PAYMENT_METHODS = [
  { value: 'account', label: 'Account / corporate' },
  { value: 'medical_aid', label: 'Medical aid' },
  { value: 'cash', label: 'Cash / private' },
  { value: 'gop', label: 'GOP' },
  { value: 'pop', label: 'POP' },
];

const PRIORITIES = [
  { value: 'p1', label: 'P1' },
  { value: 'p2', label: 'P2' },
  { value: 'p3', label: 'P3' },
  { value: 'p4', label: 'P4' },
];

// --- small field primitives -------------------------------------------------

const LABEL = 'mb-1 block text-xs font-medium text-slate-300';
const INPUT =
  'w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 ' +
  'placeholder:text-slate-500 focus:border-sky-500 focus:outline-none';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      {children}
    </div>
  );
}

function TextField({
  label,
  value,
  placeholder,
  multiline,
  onChange,
  onBlur,
}: {
  label: string;
  value: string;
  placeholder?: string;
  multiline?: boolean;
  onChange: (v: string) => void;
  onBlur?: () => void;
}) {
  return (
    <Field label={label}>
      {multiline ? (
        <textarea
          className={`${INPUT} min-h-[68px] resize-y`}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      ) : (
        <input
          className={INPUT}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
        />
      )}
    </Field>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label}>
      <select className={INPUT} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

/**
 * Yes/No/Unknown as three buttons rather than a dropdown.
 *
 * One click instead of click-scroll-click, and the answer stays readable at a
 * glance. "Unknown" is a real answer here, not a blank: it means the question
 * was asked and the caller could not say.
 */
function TriState({
  label,
  value,
  options = YES_NO,
  onChange,
}: {
  label: string;
  value: string;
  options?: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label}>
      <div className="flex flex-wrap gap-1">
        {options.map((o) => {
          const active = value === o.value;
          const danger = active && (o.value === 'no' || o.value === 'not_breathing');
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(active ? '' : o.value)}
              className={
                'rounded px-2.5 py-1 text-xs font-medium transition-colors ' +
                (active
                  ? danger
                    ? 'bg-red-600 text-white'
                    : 'bg-sky-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700')
              }
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </Field>
  );
}

function SaveIndicator({ state, error }: { state: SaveState; error: string | null }) {
  if (state === 'error') {
    return (
      <span className="text-xs font-medium text-red-400" role="alert">
        ⚠ Not saved — {error ?? 'unknown error'}
      </span>
    );
  }
  if (state === 'saving') return <span className="text-xs text-slate-400">Saving…</span>;
  if (state === 'saved') return <span className="text-xs text-emerald-400">Saved</span>;
  return <span className="text-xs text-slate-500">Autosaves as you type</span>;
}

// --- client lookup ----------------------------------------------------------

/**
 * Find a corporate client by name or by the membership number we issued.
 *
 * One field for both, because a caller says whichever they have to hand. The API
 * ranks an exact membership number above a name that merely contains the digits.
 */
function ClientSearch({
  selected,
  onSelect,
}: {
  selected: { id: string; label: string } | null;
  onSelect: (client: ClientSummary | null) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ClientSummary[]>([]);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const mine = ++seq.current;
    const t = setTimeout(() => {
      api
        .get<{ clients: ClientSummary[] }>(`/clients/search?q=${encodeURIComponent(query)}`)
        .then((r) => {
          // Ignore a slow response that lost the race to a newer keystroke.
          if (mine === seq.current) setResults(r.clients);
        })
        .catch(() => {
          if (mine === seq.current) setResults([]);
        });
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  if (selected) {
    return (
      <Field label="Client">
        <div className="flex items-center justify-between rounded border border-slate-700 bg-slate-900 px-2 py-1.5">
          <span className="text-sm text-slate-100">{selected.label}</span>
          <button
            type="button"
            className="text-xs text-slate-400 hover:text-slate-200"
            onClick={() => {
              onSelect(null);
              setQuery('');
            }}
          >
            Change
          </button>
        </div>
      </Field>
    );
  }

  return (
    <Field label="Client">
      <div className="relative">
        <input
          className={INPUT}
          value={query}
          placeholder="Company name or MR number"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {open && results.length > 0 && (
          <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded border border-slate-700 bg-slate-900 shadow-lg">
            {results.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-2 py-1.5 text-left text-sm text-slate-200 hover:bg-slate-800"
                  onClick={() => {
                    onSelect(c);
                    setOpen(false);
                  }}
                >
                  <span>{c.legal_name}</span>
                  {c.membership_number && (
                    <span className="ml-2 font-mono text-xs text-sky-400">
                      {c.membership_number}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Field>
  );
}

// --- the card ---------------------------------------------------------------

type Detail = Record<string, unknown>;

function group(detail: Detail, key: string): Record<string, unknown> {
  const value = detail[key];
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

/**
 * Read a scalar out of the detail blob for display.
 *
 * Only strings and numbers become text. Anything else — an object, an array —
 * returns empty rather than rendering "[object Object]" into an input the
 * call-taker would then save back over the real value.
 */
function str(source: Record<string, unknown>, key: string): string {
  const v = source[key];
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
}

export function CallCard({
  caseId,
  initialCase,
  initialDetail,
  initialOnScenePhone = '',
  onSaved,
}: {
  caseId: string;
  initialCase: Record<string, unknown>;
  initialDetail: Record<string, unknown>;
  /** From the case's on_scene_contact party, if one has been captured. */
  initialOnScenePhone?: string;
  onSaved?: () => void;
}) {
  const [detail, setDetail] = useState<Detail>(initialDetail);
  const [onScenePhone, setOnScenePhone] = useState(initialOnScenePhone);
  const [spine, setSpine] = useState<Record<string, unknown>>(initialCase);
  const [client, setClient] = useState<{ id: string; label: string } | null>(() => {
    const id = str(initialCase, 'client_id');
    if (!id) return null;
    return { id, label: str(initialCase, 'client_name') || 'Selected client' };
  });

  const onSave = useCallback(
    async (patch: CasePatch) => {
      await api.patch(`/cases/${caseId}`, patch);
      onSaved?.();
    },
    [caseId, onSaved],
  );

  const { save, saveNow, state, error } = useAutosave<CasePatch>({ onSave });

  // A call can end at any moment. Flush whatever is queued if the tab is hidden
  // or closed, rather than relying on the debounce timer surviving.
  useEffect(() => {
    const flush = () => void saveNow();
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      document.removeEventListener('visibilitychange', flush);
      void saveNow();
    };
  }, [saveNow]);

  /** Update one field inside a detail group, locally and on the server. */
  const setGroup = (groupKey: string, field: string, value: unknown) => {
    setDetail((d) => ({ ...d, [groupKey]: { ...group(d, groupKey), [field]: value } }));
    save({ detail: { [groupKey]: { [field]: value } } });
  };

  const setTop = (field: string, value: unknown) => {
    setDetail((d) => ({ ...d, [field]: value }));
    save({ detail: { [field]: value } });
  };

  const setSpineField = (field: keyof CasePatch, column: string, value: string) => {
    setSpine((s) => ({ ...s, [column]: value }));
    save({ [field]: value });
  };

  const triage = group(detail, 'triage');
  const access = group(detail, 'access');
  const clinical = group(detail, 'clinical');
  const payer = group(detail, 'payer');

  const SECTION = 'rounded-lg border border-slate-800 bg-slate-900/40 p-4';
  const HEADING = 'mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className={`${HEADING} mb-0`}>Call card</h3>
        <SaveIndicator state={state} error={error} />
      </div>

      {/* Where — asked first, because it is what gets an ambulance moving. */}
      <section className={SECTION}>
        <h4 className={HEADING}>Location</h4>
        <div className="space-y-3">
          <AddressField
            label="Exact location"
            value={str(spine, 'from_address')}
            placeholder="Street address, or a description of where they are"
            onChange={(v) => setSpineField('fromAddress', 'from_address', v)}
            onResolve={(place) => {
              // One save with all three, not three saves. Autosave coalesces by
              // field, and the coordinates arriving in a later request than the
              // address is how a case ends up geocoded to the previous address.
              setSpine((s) => ({
                ...s,
                from_address: place.formattedAddress,
                from_latitude: place.latitude,
                from_longitude: place.longitude,
              }));
              save({
                fromAddress: place.formattedAddress,
                // Sent as strings: numeric(9,6) via a JS number loses the last
                // digit of precision at these magnitudes, which is about 10cm —
                // harmless here, but the column is the source of truth for
                // nearest-vehicle and there is no reason to round it at all.
                ...(place.latitude !== null ? { fromLatitude: String(place.latitude) } : {}),
                ...(place.longitude !== null ? { fromLongitude: String(place.longitude) } : {}),
              });
              void saveNow();
            }}
            onBlur={() => void saveNow()}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="Nearest landmark"
              value={str(access, 'nearestLandmark')}
              placeholder="Opposite the Engen, blue gate…"
              onChange={(v) => setGroup('access', 'nearestLandmark', v)}
            />
            <TextField
              label="Access information"
              value={str(access, 'accessInformation')}
              placeholder="Gate code, which entrance, dogs"
              onChange={(v) => setGroup('access', 'accessInformation', v)}
            />
          </div>
        </div>
      </section>

      {/* What happened */}
      <section className={SECTION}>
        <h4 className={HEADING}>What has happened</h4>
        <div className="space-y-3">
          <TextField
            label="Caller's account"
            value={str(triage, 'whatHappened')}
            multiline
            placeholder="In the caller's own words"
            onChange={(v) => setGroup('triage', 'whatHappened', v)}
          />
          <TextField
            label="Main symptoms or injuries"
            value={str(triage, 'mainComplaint')}
            placeholder="Chest pain, fall, assault…"
            onChange={(v) => setGroup('triage', 'mainComplaint', v)}
          />
        </div>
      </section>

      {/* Triage — the questions that decide priority and crew */}
      <section className={SECTION}>
        <h4 className={HEADING}>Triage</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <TriState
            label="Is the scene safe?"
            value={str(triage, 'sceneSafe')}
            onChange={(v) => setGroup('triage', 'sceneSafe', v || undefined)}
          />
          <TriState
            label="Awake / alert?"
            value={str(triage, 'awakeAlert')}
            onChange={(v) => setGroup('triage', 'awakeAlert', v || undefined)}
          />
          <TriState
            label="Breathing"
            value={str(triage, 'breathing')}
            options={BREATHING}
            onChange={(v) => setGroup('triage', 'breathing', v || undefined)}
          />
          <TriState
            label="Severe bleeding or pain?"
            value={str(triage, 'severeBleedingOrPain')}
            onChange={(v) => setGroup('triage', 'severeBleedingOrPain', v || undefined)}
          />
        </div>
      </section>

      {/* Who */}
      <section className={SECTION}>
        <h4 className={HEADING}>Patient</h4>
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label="Number of patients">
            <input
              type="number"
              min={0}
              className={INPUT}
              value={str(triage, 'patientCount')}
              onChange={(e) =>
                setGroup(
                  'triage',
                  'patientCount',
                  e.target.value === '' ? undefined : Number(e.target.value),
                )
              }
            />
          </Field>
          <SelectField
            label="Sex"
            value={str(triage, 'patientSex')}
            options={[
              { value: 'female', label: 'Female' },
              { value: 'male', label: 'Male' },
              { value: 'unknown', label: 'Unknown' },
            ]}
            onChange={(v) => setGroup('triage', 'patientSex', v || undefined)}
          />
          <Field label="Age (years)">
            <input
              type="number"
              min={0}
              max={130}
              className={INPUT}
              placeholder="If known"
              value={str(triage, 'ageYears')}
              onChange={(e) =>
                setGroup(
                  'triage',
                  'ageYears',
                  e.target.value === '' ? undefined : Number(e.target.value),
                )
              }
            />
          </Field>
          <SelectField
            label="Age range"
            value={str(triage, 'ageBand')}
            options={AGE_BANDS}
            onChange={(v) => setGroup('triage', 'ageBand', v || undefined)}
          />
        </div>
      </section>

      {/* Response */}
      <section className={SECTION}>
        <h4 className={HEADING}>Response</h4>
        <div className="grid gap-3 sm:grid-cols-3">
          <SelectField
            label="Priority"
            value={str(spine, 'priority')}
            options={PRIORITIES}
            onChange={(v) => setSpineField('priority', 'priority', v)}
          />
          <SelectField
            label="Level of care required"
            value={str(clinical, 'requiredLevelOfCare')}
            options={LEVELS_OF_CARE}
            onChange={(v) => setGroup('clinical', 'requiredLevelOfCare', v || undefined)}
          />
          <SelectField
            label="Primary or transfer"
            value={str(detail, 'responseKind')}
            options={[
              { value: 'primary', label: 'Primary response' },
              { value: 'transfer', label: 'Transfer' },
            ]}
            onChange={(v) => setTop('responseKind', v || undefined)}
          />
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <TextField
            label="On-scene contact number"
            value={onScenePhone}
            placeholder="Number to call back"
            onChange={(v) => {
              setOnScenePhone(v);
              save({ onSceneContact: { phone: v } });
            }}
          />
          <TextField
            label="Authorising manager"
            value={str(detail, 'authorisingManager')}
            placeholder="N/A on most calls"
            onChange={(v) => setTop('authorisingManager', v)}
          />
        </div>
      </section>

      {/* Money */}
      <section className={SECTION}>
        <h4 className={HEADING}>Who pays</h4>
        <div className="space-y-3">
          <ClientSearch
            selected={client}
            onSelect={(c) => {
              setClient(c ? { id: c.id, label: c.legal_name } : null);
              if (c) save({ clientId: c.id });
            }}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label="Payment method"
              value={str(payer, 'paymentMethod')}
              options={PAYMENT_METHODS}
              onChange={(v) => setGroup('payer', 'paymentMethod', v || undefined)}
            />
          </div>
          <SchemePicker
            value={str(payer, 'medicalAidName')}
            administrator={str(payer, 'medicalAidAdministrator') || null}
            // Drives which of the four authorisation rules applies. Undefined
            // until the dispatcher has said whether this is a transfer, and the
            // picker deliberately says nothing rather than guessing.
            responseKind={(str(detail, 'responseKind') as 'primary' | 'transfer' | '') || undefined}
            onSelect={(s) => {
              // Both fields in ONE save. The administrator is derived from the
              // scheme, so writing them in separate requests leaves a window
              // where the card holds a scheme with the previous scheme's
              // administrator — and that window is exactly when somebody reads
              // it off the screen.
              setDetail((d) => ({
                ...d,
                payer: {
                  ...group(d, 'payer'),
                  medicalAidName: s.name,
                  medicalAidAdministrator: s.administrator ?? undefined,
                },
              }));
              save({
                detail: {
                  payer: {
                    medicalAidName: s.name,
                    ...(s.administrator ? { medicalAidAdministrator: s.administrator } : {}),
                  },
                },
              });
            }}
          />
        </div>
      </section>

      {/* Last, on purpose. These are the columns the monthly board report is
          built from; they do not get an ambulance moving, and putting them
          above the clinical questions would slow every call to serve a report
          nobody reads until the 1st. */}
      <OccFields
        spine={spine}
        onPatch={(patch) => {
          // Mirror locally so the derived project number appears immediately,
          // rather than after the save round-trips.
          setSpine((s) => ({
            ...s,
            ...(patch.callSourceId !== undefined ? { call_source_id: patch.callSourceId } : {}),
            ...(patch.projectNumber !== undefined ? { project_number: patch.projectNumber } : {}),
            ...(patch.natureOfIncident !== undefined
              ? { nature_of_incident: patch.natureOfIncident }
              : {}),
            ...(patch.serviceType !== undefined ? { service_type: patch.serviceType } : {}),
            ...(patch.servicedBy !== undefined ? { serviced_by: patch.servicedBy } : {}),
            ...(patch.spActivationReason !== undefined
              ? { sp_activation_reason: patch.spActivationReason }
              : {}),
            ...(patch.billable !== undefined ? { billable: patch.billable } : {}),
          }));
          save(patch);
        }}
      />
    </div>
  );
}
